import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useUIStore } from './store/uiStore';
import { useBoardStore } from './store/boardStore';
import { ToastProvider, useToast } from './contexts/ToastContext';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CanvasBoardPage from './pages/CanvasBoardPage';
import ProfilePage from './pages/ProfilePage';
import TeamsPage from './pages/TeamsPage';
import TeamJoinPage from './pages/TeamJoinPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ActionsPage from './pages/ActionsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import OrganizationPage from './pages/OrganizationPage';
import FeaturesPage from './pages/FeaturesPage';
import TemplatesPage from './pages/TemplatesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AlternativesPage from './pages/AlternativesPage';
import AlternativeComparisonPage from './pages/AlternativeComparisonPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AiRetrospectiveSummaryPage from './pages/AiRetrospectiveSummaryPage';
import FreeAiReportPage from './pages/FreeAiReportPage';
import TemplateDetailPage from './pages/TemplateDetailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AiInsightsDashboardPage from './pages/AiInsightsDashboardPage';
import SamlCallbackPage from './pages/SamlCallbackPage';

// Components
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/ToastContainer';
import LegacyRedirect from './components/LegacyRedirect';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Inner component that handles routing with location access
function AppRoutes() {
  const location = useLocation();
  const { isAuthenticated, isInitializing, user } = useAuthStore();
  const { reset } = useBoardStore();
  const { toasts, removeToast } = useToast();
  const { sidebarCollapsed } = useUIStore();

  // Check if current route is board/canvas mode (full screen, no navbar)
  const isCanvasMode = location.pathname.startsWith('/app/board/');

  // On mount: restore access token from HttpOnly cookie, then refresh user profile.
  // Read state directly from store (not reactive values) so this only runs once on mount,
  // not on every isAuthenticated change (e.g. after login).
  const { silentRefresh } = useAuthStore();
  useGoogleOneTap();

  useEffect(() => {
    const { isAuthenticated: isAuth } = useAuthStore.getState();
    if (isAuth) {
      // Access token is in-memory only – get a fresh one from cookie on page load
      silentRefresh().then(() => {
        const { refreshUser: doRefresh } = useAuthStore.getState();
        doRefresh();
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: run only once on mount, not on every auth state change

  useEffect(() => {
    // Reset board store when the logged-in user actually changes (login/logout/switch).
    // Keyed on user?.id rather than the user object itself: refreshUser() replaces
    // `user` with a new object reference on every profile refetch even when the
    // underlying data is unchanged, which previously wiped boardStore.boards
    // (via reset()) shortly after a fast fetchBoards() had just populated it.
    return () => {
      reset();
    };
  }, [user?.id, reset]);

  useEffect(() => {
    if (!window.gtag) return;
    if (user?.id) {
      window.gtag('set', { user_id: user.id });
      window.gtag('config', 'G-4WP1WELB2N', { user_id: user.id });
    } else {
      window.gtag('set', { user_id: undefined });
    }
  }, [user?.id]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Block protected page rendering until silentRefresh completes
  if (isInitializing) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" />;
  }

  // NOT: Global loading state'i burada kullanmıyoruz çünkü 
  // form submit sırasında tüm route'ları unmount eder ve form state'i kaybeder.
  // Her sayfa kendi loading state'ini yönetmeli.

  // Check if we should show sidebar (authenticated and not on landing pages)
  const isLandingPage = ['/', '/features', '/templates', '/pricing', '/about', '/login', '/register', '/privacy', '/terms', '/refund-policy', '/ai-retrospective-summary', '/auth/callback', '/auth/saml/callback'].includes(location.pathname) || location.pathname.startsWith('/blog') || location.pathname.startsWith('/alternatives') || location.pathname.startsWith('/templates/') || location.pathname.startsWith('/tools/');
  const showSidebar = isAuthenticated && !isCanvasMode && !isLandingPage;

  return (
    <div className={isCanvasMode ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-900'}>
      {!isCanvasMode && <Navbar sidebar={showSidebar} />}
      <main className={`transition-all duration-300 ease-in-out ${showSidebar ? (sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64') : ''}`}>
        <Routes>
          {/* Public routes */}
          {/* Root always shows the marketing homepage, even when logged in — the
              app lives at /app/*, so retrosive.com stays browsable for everyone. */}
          <Route path="/" element={<HomePage />} />
          {/* Static marketing pages - accessible to everyone */}
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/:slug" element={<TemplateDetailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/alternatives" element={<AlternativesPage />} />
          <Route path="/alternatives/:slug" element={<AlternativeComparisonPage />} />
          <Route path="/ai-retrospective-summary" element={<AiRetrospectiveSummaryPage />} />
          <Route path="/tools/ai-retrospective-report-generator" element={<FreeAiReportPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/saml/callback" element={<SamlCallbackPage />} />

          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/app/dashboard" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/app/dashboard" replace />
              ) : (
                <RegisterPage />
              )
            }
          />
          <Route
            path="/teams/join/:token"
            element={<TeamJoinPage />}
          />
          <Route
            path="/accept-invite/:token"
            element={<AcceptInvitePage />}
          />

          {/* Protected app routes, namespaced under /app so retrosive.com/ stays
              a browsable marketing site for logged-in users too. */}
          <Route
            path="/app/dashboard"
            element={
              isAuthenticated ? (
                <DashboardPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/board/:boardId"
            element={
              isAuthenticated ? (
                <CanvasBoardPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/profile"
            element={
              isAuthenticated ? (
                <ProfilePage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/teams"
            element={
              isAuthenticated ? (
                <TeamsPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/actions"
            element={
              isAuthenticated ? (
                <ActionsPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/subscription"
            element={
              isAuthenticated ? (
                <SubscriptionPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/app/organization"
            element={isAuthenticated ? <OrganizationPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/app/organization/:orgId"
            element={isAuthenticated ? <OrganizationPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/app/organization/:orgId/:tab"
            element={isAuthenticated ? <OrganizationPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/app/insights"
            element={
              isAuthenticated ? (
                <AiInsightsDashboardPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Legacy bookmarks/emails pointing at pre-/app URLs — forward, don't 404. */}
          <Route path="/dashboard" element={<LegacyRedirect />} />
          <Route path="/board/:boardId" element={<LegacyRedirect />} />
          <Route path="/profile" element={<LegacyRedirect />} />
          <Route path="/teams" element={<LegacyRedirect />} />
          <Route path="/actions" element={<LegacyRedirect />} />
          <Route path="/subscription" element={<LegacyRedirect />} />
          <Route path="/organization" element={<LegacyRedirect />} />
          <Route path="/organization/:orgId" element={<LegacyRedirect />} />
          <Route path="/organization/:orgId/:tab" element={<LegacyRedirect />} />
          <Route path="/insights" element={<LegacyRedirect />} />

          {/* Catch all route */}
          <Route 
            path="*" 
            element={<Navigate to="/" replace />} 
          />
        </Routes>
      </main>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
}

// Main App component with providers
function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
